import { toJS, makeAutoObservable } from 'mobx'
import { RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import {
  type IChatbotActionSendMessage,
  type IChatbotActionSendMessageSetting,
  ChatbotActionType,
  type IChatbotActionType,
  type IChatbotActionSettings,
} from './type'

import {
  ChatbotActionNote,
  ChatbotActionTag,
  type IChatbotAction,
  type IChatbotActionModel,
} from './ChatbotActions'

export class AgentActionSendMessage implements IChatbotAction {
  static Create() {
    return new AgentActionSendMessage({
      type: ChatbotActionType.SendMessage,
      settings: {
        message: '',
        media_url: [],
        type: 'manual',
      },
    })
  }

  private _type = ChatbotActionType.SendMessage
  private _message = ''
  private _attachments: Attachment[] = []
  private _isAutoMessageType = false

  constructor(item: IChatbotActionSendMessageSetting) {
    this.syncOrigin(item.settings)

    makeAutoObservable(this)
  }

  get isAutoMessageType() {
    return this._isAutoMessageType
  }

  get type() {
    return this._type
  }

  get message() {
    return this._message
  }

  get attachments() {
    return this._attachments
  }

  setIsAutoMessageType = (isAutoMessageType: boolean) => {
    this._isAutoMessageType = isAutoMessageType
  }

  sync(message: string, attachments: Attachment[]) {
    this._message = message
    this._attachments = attachments
  }

  syncOrigin(item: IChatbotActionSendMessage) {
    this._message = item?.message ?? ''
    this._attachments =
      item?.media_url?.map((item) => new Attachment({ requestAttachment: item })) ?? []
    this._isAutoMessageType = item?.type === 'auto' ? true : false
  }

  toJSON(): IChatbotActionSendMessageSetting {
    return toJS({
      type: ChatbotActionType.SendMessage,
      settings: {
        message: this.message,
        media_url: this.attachments.map((attachment) => new RequestAttachment(attachment)),
        type: this._isAutoMessageType ? 'auto' : 'manual',
      },
    })
  }
}

export class AgentActions {
  static _actionFactory(type: IChatbotActionType): IChatbotActionModel {
    switch (type) {
      case ChatbotActionType.AddTag:
      case ChatbotActionType.RemoveTag:
        return ChatbotActionTag.Create(type)
      case ChatbotActionType.SendMessage:
        return AgentActionSendMessage.Create()
      case ChatbotActionType.ContactNote:
        return ChatbotActionNote.Create()
      default:
        throw new TypeError(`Unknown chatbot actin type: "${type}"`)
    }
  }

  actions: IChatbotActionModel[] = []

  constructor(items?: IChatbotActionSettings[]) {
    if (!!items) this.syncOrigin(items)

    makeAutoObservable(this)
  }

  addAction(type: IChatbotActionType) {
    const actions = Array.from(this.actions)
    const action = AgentActions._actionFactory(type)

    actions.push(action)
    this.actions = actions

    return action
  }

  removeAction(index: number) {
    const actions = Array.from(this.actions)
    const [action] = actions.splice(index, 1)

    this.actions = actions

    return action
  }

  syncOrigin(items?: IChatbotActionSettings[] | null) {
    this.actions =
      (items
        ?.map((item) => {
          switch (item.type) {
            case ChatbotActionType.AddTag:
              return new ChatbotActionTag(item)
            case ChatbotActionType.RemoveTag:
              return new ChatbotActionTag(item)
            case ChatbotActionType.SendMessage:
              return new AgentActionSendMessage(item)
            case ChatbotActionType.ContactNote:
              return new ChatbotActionNote(item)
            default:
              return null
          }
        })
        .filter(Boolean) as AgentActions['actions']) ?? []
  }

  toJSON(): IChatbotActionSettings[] {
    return toJS(this.actions.map((action) => action.toJSON()))
  }

  reset() {
    this.actions = []
  }
}
